/** Half-open byte interval [start, end). TYPE-002 */
export interface ByteSpan {
  readonly start: number;
  readonly end: number;
}

export type DownloadState =
  | 'queued'
  | 'probing'
  | 'active'
  | 'pausing'
  | 'paused'
  | 'verifying'
  | 'complete'
  | 'recoverableError'
  | 'fatalError'
  | 'deleted';

/** Priority classes, lower number = served first. ALG-031 */
export const Priority = {
  Bootstrap: 0,
  PlaybackEmergency: 1,
  ImminentPlayback: 2,
  ParserMetadata: 3,
  ExportCompletion: 4,
  SequentialFill: 5,
  SpeculativePrefetch: 6,
} as const;
export type PriorityClass = (typeof Priority)[keyof typeof Priority];

/** Which of the three user-facing download modes is active. UI-007 */
export type DownloadMode = 'preview' | 'balanced' | 'download';

export type RangeProvenance =
  | 'unknown'
  /** Server answered a ranged GET with a valid 206 + Content-Range. */
  | 'proven'
  /** Server answered a ranged GET with a full 200. */
  | 'ignored'
  /** Server advertised ranges but a ranged request failed. */
  | 'advertised-unproven';

/** Representation identity evidence only, never access tokens. TYPE-004, TYPE-039 */
export interface SourceIdentity {
  /** Fingerprint of the final URL without query values (host + path hash). */
  urlFingerprint: string;
  totalSize: number | null;
  etag: string | null;
  strongEtag: boolean;
  lastModified: string | null;
}

export interface Demand {
  requestId: number;
  span: ByteSpan;
  priority: PriorityClass;
  reason: string;
  epoch: number;
  /** Lower value = more urgent within the same class (e.g. seconds until needed). */
  deadline?: number;
}

export type ErrorCode =
  | 'INVALID_URL'
  | 'UNSUPPORTED_SCHEME'
  | 'CREDENTIALS_IN_URL'
  | 'WEBPAGE_NOT_FILE'
  | 'CORS_OR_NETWORK'
  | 'HTTP_FORBIDDEN'
  | 'HTTP_NOT_FOUND'
  | 'HTTP_EXPIRED'
  | 'HTTP_STATUS'
  | 'RANGE_NOT_SATISFIABLE'
  | 'RANGE_UNSUPPORTED'
  | 'BAD_CONTENT_RANGE'
  | 'CONTENT_ENCODING'
  | 'TRUNCATED_BODY'
  | 'SOURCE_CHANGED'
  | 'OPAQUE_REDIRECT'
  | 'QUOTA_EXCEEDED'
  | 'STORAGE_UNAVAILABLE'
  | 'STORAGE_CORRUPT'
  | 'DECODE_UNSUPPORTED'
  | 'PREVIEW_FAILED'
  | 'EXPORT_FAILED'
  | 'WORKER_CRASHED'
  | 'EMPTY_FILE'
  | 'ABORTED'
  | 'UNKNOWN';

/** TYPE-012 */
export interface AppError {
  code: ErrorCode;
  recoverable: boolean;
  summary: string;
  action: string;
  /** Already redacted. */
  meta?: Record<string, string | number | boolean | null>;
}

/** The four independent capability flags. ARCH-015 */
export interface SessionCapabilities {
  sourceReadable: boolean;
  canContinue: boolean;
  previewPossible: boolean;
  exportable: boolean;
}

export type MediaKind =
  | 'video'
  | 'audio'
  | 'image'
  | 'pdf'
  | 'text'
  | 'markdown'
  | 'json'
  | 'csv'
  | 'xml'
  | 'docx'
  | 'xlsx'
  | 'archive'
  | 'unknown';

export interface Counters {
  /** Response payload bytes received over the network, including retries. */
  transferredBytes: number;
  /** Union of durable committed spans. */
  committedBytes: number;
  /** Network requests started. */
  requests: number;
  failedRequests: number;
  retriedBytes: number;
}

export interface SessionSnapshot {
  sessionId: string;
  epoch: number;
  state: DownloadState;
  identity: SourceIdentity | null;
  rangeProvenance: RangeProvenance;
  counters: Counters;
  /** Normalized spans that can be read back right now (capped). Durable bytes are counters.committedBytes. */
  readable: ByteSpan[];
  inFlight: ByteSpan[];
  bytesPerSecond: number | null;
  error: AppError | null;
  mode: DownloadMode;
  /** True once a ranged request has been proven to work. */
  seekable: boolean;
  /** Response headers readable under CORS. */
  exposedHeaders: {
    contentRange: boolean;
    etag: boolean;
    lastModified: boolean;
    contentLength: boolean;
  };
  contentType: string | null;
  contentDispositionName: string | null;
  finalHost: string | null;
}

export const MAX_SAFE = Number.MAX_SAFE_INTEGER;
